import { useRef, useState, useEffect, useCallback } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';

import {
  Popover,
  TouchDialog,
  Icon,
  registerIcon,
  useOuterEvent,
  Menu,
  Button,
  defaultThemeProp,
  useI18n,
  Tooltip,
  isInstance,
  isTouchDevice
} from '@pega/cosmos-react-core';
import type { TranslationPack } from '@pega/cosmos-react-core';
import * as arrowMicroDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/arrow-micro-down.icon';
import * as alignLeftIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/align-left.icon';
import * as alignCenterIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/align-center.icon';
import * as alignRightIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/align-right.icon';

import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(arrowMicroDownIcon, alignLeftIcon, alignCenterIcon, alignRightIcon);

const StyledAlignmentSelect = styled(Button)(({ theme }) => {
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    height: calc(3.5 * ${theme.base.spacing});
    padding: 0 calc(0.5 * ${theme.base.spacing});
  `;
});

StyledAlignmentSelect.defaultProps = defaultThemeProp;

const StyledSelectMenu = styled.div(({ theme }) => {
  return css`
    min-width: calc(20 * ${theme.base.spacing});
  `;
});

StyledSelectMenu.defaultProps = defaultThemeProp;

type AlignmentOption = 'left' | 'center' | 'right';

const ALIGNMENTS: { id: AlignmentOption; icon: string; labelKey: keyof TranslationPack }[] = [
  { id: 'left', icon: 'align-left', labelKey: 'rte_align_left' },
  { id: 'center', icon: 'align-center', labelKey: 'rte_align_center' },
  { id: 'right', icon: 'align-right', labelKey: 'rte_align_right' }
];

interface AlignmentSelectProps {
  editor: TiptapEditor;
  'data-testid'?: string;
}

const AlignmentSelect = ({ editor, ...restProps }: AlignmentSelectProps) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [activeAlignment, setActiveAlignment] = useState<AlignmentOption>('left');
  const selecting = useRef(false);
  const openedByClick = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);

  useOuterEvent('mousedown', [buttonRef, popoverRef, menuRef, touchDialogRef], () => {
    setOpen(false);
  });

  const handleEscapeClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const updateActiveAlignment = useCallback(() => {
    if (editor.isActive({ textAlign: 'center' })) {
      setActiveAlignment('center');
    } else if (editor.isActive({ textAlign: 'right' })) {
      setActiveAlignment('right');
    } else {
      setActiveAlignment('left');
    }
  }, [editor]);

  useEffect(() => {
    updateActiveAlignment();
    editor.on('selectionUpdate', updateActiveAlignment);
    editor.on('transaction', updateActiveAlignment);
    return () => {
      editor.off('selectionUpdate', updateActiveAlignment);
      editor.off('transaction', updateActiveAlignment);
    };
  }, [editor, updateActiveAlignment]);

  const onAlignmentSelect = (alignmentId: string, e: MouseEvent<Element>) => {
    e.stopPropagation();
    e.preventDefault();

    editor.chain().focus().setTextAlign(alignmentId).run();

    if (!openedByClick.current) {
      buttonRef.current?.focus();
    }
    setOpen(false);
    selecting.current = true;
    setTimeout(() => {
      selecting.current = false;
      if (!editor.isFocused && openedByClick.current) {
        editor.commands.focus();
      }
    }, 0);
  };

  const getActiveIcon = () => {
    const alignment = ALIGNMENTS.find(a => a.id === activeAlignment);
    return alignment?.icon || 'align-left';
  };

  const getActiveLabel = () => {
    const alignment = ALIGNMENTS.find(a => a.id === activeAlignment);
    return alignment ? t(alignment.labelKey) : t('rte_align_left');
  };

  const menuContent = (
    <Menu
      items={ALIGNMENTS.map(({ id, icon, labelKey }) => ({
        id,
        primary: t(labelKey),
        visual: <Icon name={icon} />,
        selected: id === activeAlignment
      }))}
      focusControlEl={isTouchDevice ? undefined : buttonRef.current ?? undefined}
      mode='single-select'
      ref={menuRef}
      onItemClick={onAlignmentSelect}
    />
  );

  return (
    <>
      <StyledAlignmentSelect
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          if (!open) {
            openedByClick.current = true;
          }
          setOpen(!open);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !selecting.current) {
            e.preventDefault();
            setOpen(true);
            openedByClick.current = false;
          } else if (e.key === 'Enter') {
            e.preventDefault();
            selecting.current = false;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            setOpen(false);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          const { relatedTarget } = e;
          if (
            isInstance(relatedTarget, Node) &&
            !buttonRef.current?.contains(relatedTarget) &&
            !popoverRef.current?.contains(relatedTarget) &&
            !menuRef.current?.contains(relatedTarget) &&
            !touchDialogRef.current?.contains(relatedTarget)
          ) {
            setOpen(false);
          }
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        aria-expanded={open}
        aria-label={`${t('rte_alignment')}. ${getActiveLabel()} ${t('selected').toLocaleLowerCase()}`}
        aria-haspopup
      >
        <Icon name={getActiveIcon()} />
        <Icon name='arrow-micro-down' />
      </StyledAlignmentSelect>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_alignment')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={t('rte_alignment')}
          onDismiss={() => setOpen(false)}
        >
          {menuContent}
        </TouchDialog>
      ) : (
        <Popover
          show={open}
          as={StyledSelectMenu}
          target={buttonRef.current}
          placement='bottom-start'
          ref={popoverRef}
        >
          {menuContent}
        </Popover>
      )}
    </>
  );
};

export default AlignmentSelect;
